// src/components/SupplierAccount.js

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/SupplierAccount.css';
import moment from 'moment-timezone';
import api from '../api/api';

// IMPORTS PARA GENERAR WORD SIN TABLAS, CON CALIBRI, FUENTES GRANDES Y TABULACIONES
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  TabStopType,
} from 'docx';
import { saveAs } from 'file-saver';

// Función para formatear moneda en formato ¢XXX.XXX.XX
const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '¢0.00';
  let parts = num.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return '¢' + parts.join('.');
};

const SupplierAccount = () => {
  const { id } = useParams();

  // Datos del proveedor (incluye facturas y pagos)
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados para modales
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Bloqueo anti doble envío
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const invoiceSubmittingRef = useRef(false);
  const paymentSubmittingRef = useRef(false);

  // Formulario de nueva factura
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: '',
    description: '',
    amount: '',
    invoiceDate: '',
  });

  // Formulario de nuevo pago
  const [paymentForm, setPaymentForm] = useState({
    invoiceId: '',
    amount: '',
    bank: '',
    voucherNumber: '',
    description: '',
  });

  useEffect(() => {
    fetchSupplier();
    // eslint-disable-next-line
  }, [id]);

  const fetchSupplier = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/suppliers/${id}`);
      setSupplier(res.data);
    } catch (error) {
      console.error('Error al obtener proveedor:', error);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Función PARA DESCARGAR CUENTA en Word
  // -------------------------------------------------------------------------
  const handleDownloadAccount = async () => {
    if (!supplier) return;

    // Sólo facturas y pagos visibles
    const allInvoices = supplier.invoices.filter((inv) => inv.status !== 'hidden');
    const visiblePayments = supplier.payments.filter((p) => !p.hidden);

    // Definir tabulaciones (en twentieths of a point)
    const tabStops = [
      { type: TabStopType.LEFT, position: 1440 }, // 1" - Monto
      { type: TabStopType.LEFT, position: 2880 }, // 2" - Fecha
      { type: TabStopType.LEFT, position: 4320 }, // 3" - Estado
    ];

    const children = [];

    // ----- TÍTULO -----
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Detalle del Proveedor: ${supplier.name.toUpperCase()}`,
            bold: true,
            size: 48, // 24pt
            font: 'Calibri',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 },
      })
    );

    // ----- FACTURAS HEADER -----
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Facturas', bold: true, size: 36, font: 'Calibri' })],
        spacing: { after: 200 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: 'Nº Factura', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun({ text: 'Monto', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun({ text: 'Fecha', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun({ text: 'Estado', bold: true, size: 24, font: 'Calibri' }),
        ],
        tabStops,
        spacing: { after: 100 },
      })
    );

    // ----- FACTURAS LÍNEAS -----
    allInvoices.forEach((inv) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: String(inv.invoiceNumber), size: 24, font: 'Calibri' }),
            new TextRun('\t'),
            new TextRun({ text: formatCurrency(inv.amount), size: 24, font: 'Calibri' }),
            new TextRun('\t'),
            new TextRun({
              text: inv.invoiceDate
                ? moment(inv.invoiceDate).tz('America/Costa_Rica').format('DD-MM-YYYY')
                : '—',
              size: 24,
              font: 'Calibri',
            }),
            new TextRun('\t'),
            new TextRun({ text: inv.status, size: 24, font: 'Calibri' }),
          ],
          tabStops,
        })
      );
    });

    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));

    // ----- RESUMEN -----
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Resumen', bold: true, size: 36, font: 'Calibri' })],
        spacing: { after: 200 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: 'Total de Facturas: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: `${allInvoices.length}`, size: 24, font: 'Calibri' }),
        ],
      }),
      new Paragraph({
        children: [
          new TextRun({ text: 'Total Pagos Registrados: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: `${visiblePayments.length}`, size: 24, font: 'Calibri' }),
        ],
      }),
      new Paragraph({
        children: [
          new TextRun({ text: 'Saldo Pendiente: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({
            text: formatCurrency(supplier.outstandingBalance),
            size: 24,
            font: 'Calibri',
          }),
        ],
      })
    );

    const doc = new Document({ sections: [{ children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Cuenta_${supplier.name}.docx`);
  };

  // -------------------------------------------------------------------------
  // Manejo de impresión
  // -------------------------------------------------------------------------
  const handlePrint = () => window.print();

  // -------------------------------------------------------------------------
  // REGISTRAR NUEVA FACTURA
  // -------------------------------------------------------------------------
  const openInvoiceModal = () => {
    setInvoiceForm({ invoiceNumber: '', description: '', amount: '', invoiceDate: '' });
    setShowInvoiceModal(true);
  };
  const closeInvoiceModal = () => setShowInvoiceModal(false);

  const handleInvoiceFormChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm((f) => ({ ...f, [name]: value }));
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();

    if (invoiceSubmittingRef.current) return;

    if (!invoiceForm.invoiceNumber || !invoiceForm.amount || !invoiceForm.invoiceDate) {
      alert('Completa los campos obligatorios: Nº Factura, Monto y Fecha.');
      return;
    }

    try {
      invoiceSubmittingRef.current = true;
      setIsSubmittingInvoice(true);

      await api.post('/api/invoices', {
        supplierId: id,
        ...invoiceForm,
        invoiceDate: moment.tz(invoiceForm.invoiceDate, 'America/Costa_Rica').startOf('day').toDate(),
      });

      alert('Factura registrada con éxito.');
      closeInvoiceModal();
      fetchSupplier();
    } catch (err) {
      console.error(err);
      alert('Error al crear factura');
    } finally {
      invoiceSubmittingRef.current = false;
      setIsSubmittingInvoice(false);
    }
  };

  // -------------------------------------------------------------------------
  // REGISTRAR PAGO
  // -------------------------------------------------------------------------
  const openPaymentModal = () => {
    setPaymentForm({ invoiceId: '', amount: '', bank: '', voucherNumber: '', description: '' });
    setShowPaymentModal(true);
  };
  const closePaymentModal = () => setShowPaymentModal(false);

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((f) => ({ ...f, [name]: value }));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (paymentSubmittingRef.current) return;

    if (!paymentForm.invoiceId || !paymentForm.amount) {
      alert('Selecciona la factura y especifica el monto a pagar');
      return;
    }

    try {
      paymentSubmittingRef.current = true;
      setIsSubmittingPayment(true);

      await api.post('/api/payments', {
        supplierId: id,
        ...paymentForm,
      });

      alert('Pago registrado con éxito.');
      closePaymentModal();
      fetchSupplier();
    } catch (err) {
      console.error(err);
      alert('Error al registrar pago');
    } finally {
      paymentSubmittingRef.current = false;
      setIsSubmittingPayment(false);
    }
  };

  // -------------------------------------------------------------------------
  // OCULTAR (Soft Delete)
  // -------------------------------------------------------------------------
  const handleHideInvoice = async (invoiceId) => {
    if (!window.confirm('¿Deseas ocultar esta factura?')) return;

    try {
      await api.put(`/api/invoices/${invoiceId}`, { status: 'hidden' });
      alert('Factura ocultada con éxito.');
      fetchSupplier();
    } catch (err) {
      console.error(err);
      alert('Error al ocultar factura');
    }
  };

  const handleHidePayment = async (paymentId) => {
    if (!window.confirm('¿Deseas ocultar este pago?')) return;

    try {
      await api.put(`/api/payments/${paymentId}`, { hidden: true });
      alert('Pago ocultado con éxito.');
      fetchSupplier();
    } catch (err) {
      console.error(err);
      alert('Error al ocultar pago');
    }
  };

  // Filtrar datos para render
  const allInvoices = supplier?.invoices?.filter((inv) => inv.status !== 'hidden') || [];
  const visiblePayments = supplier?.payments?.filter((p) => !p.hidden) || [];

  if (loading) {
    return (
      <div className="supplier-account-container">
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="supplier-account-container">
        <p>Proveedor no encontrado.</p>
      </div>
    );
  }

  return (
    <div className="supplier-account-container">
      {/* BOTONES PARA ACCIONES (no-print) */}
      <div className="supplier-account-actions no-print">
        <button onClick={openInvoiceModal}>Registrar Factura</button>
        <button onClick={openPaymentModal}>Registrar Pago</button>
        <button onClick={handlePrint}>Imprimir</button>
        <button onClick={handleDownloadAccount}>Descargar Cuenta</button>
      </div>

      {/* ÁREA IMPRIMIBLE */}
      <div className="printable-area">
        <h1>Detalle del Proveedor: {supplier.name.toUpperCase()}</h1>

        {/* FACTURAS */}
        <h2>Facturas</h2>
        {allInvoices.length === 0 ? (
          <p>No hay facturas registradas.</p>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Nº Factura</th>
                <th>Descripción</th>
                <th>Monto</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th className="no-print">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {allInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.invoiceNumber}</td>
                  <td>{inv.description || '—'}</td>
                  <td>{formatCurrency(inv.amount)}</td>
                  <td>
                    {inv.invoiceDate
                      ? moment(inv.invoiceDate).tz('America/Costa_Rica').format('DD/MM/YYYY')
                      : '—'}
                  </td>
                  <td>{inv.status}</td>
                  <td className="no-print">
                    <button className="hide-button" onClick={() => handleHideInvoice(inv.id)}>
                      Ocultar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* PAGOS REALIZADOS */}
        <h2>Pagos Realizados</h2>
        {visiblePayments.length === 0 ? (
          <p>No hay pagos registrados.</p>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Nº Comprobante</th>
                <th>Banco del Pago</th>
                <th>Monto del Pago</th>
                <th>Fecha del Pago</th>
                <th className="no-print">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visiblePayments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.voucherNumber || '—'}</td>
                  <td>{payment.bank || '—'}</td>
                  <td>{formatCurrency(payment.amount)}</td>
                  <td>
                    {payment.paymentDate
                      ? moment(payment.paymentDate).tz('America/Costa_Rica').format('DD/MM/YYYY')
                      : '—'}
                  </td>
                  <td className="no-print">
                    <button className="hide-button" onClick={() => handleHidePayment(payment.id)}>
                      Ocultar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* RESUMEN */}
        <div className="supplier-account-summary">
          <h3>Resumen</h3>
          <p>Total de Facturas: {allInvoices.length}</p>
          <p>Total Pagos Registrados: {visiblePayments.length}</p>
          <p>Saldo Pendiente: {formatCurrency(supplier.outstandingBalance)}</p>
        </div>
      </div>

      {/* MODAL - Registrar Factura */}
      {showInvoiceModal && (
        <div className="sa-modal-overlay">
          <div className="sa-modal">
            <h2>Registrar Nueva Factura</h2>
            <form onSubmit={handleInvoiceSubmit}>
              <label>Nº Factura *</label>
              <input
                type="text"
                name="invoiceNumber"
                value={invoiceForm.invoiceNumber}
                onChange={handleInvoiceFormChange}
                required
              />

              <label>Descripción</label>
              <textarea
                name="description"
                value={invoiceForm.description}
                onChange={handleInvoiceFormChange}
              />

              <label>Monto *</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={invoiceForm.amount}
                onChange={handleInvoiceFormChange}
                required
              />

              <label>Fecha de Factura *</label>
              <input
                type="date"
                name="invoiceDate"
                value={invoiceForm.invoiceDate}
                onChange={handleInvoiceFormChange}
                required
              />

              <div className="sa-modal-actions">
                <button type="submit" disabled={isSubmittingInvoice}>
                  Guardar
                </button>
                <button type="button" onClick={closeInvoiceModal} disabled={isSubmittingInvoice}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL - Registrar Pago */}
      {showPaymentModal && (
        <div className="sa-modal-overlay">
          <div className="sa-modal">
            <h2>Registrar Pago</h2>
            <form onSubmit={handlePaymentSubmit}>
              <label>Seleccione Factura *</label>
              <select
                name="invoiceId"
                value={paymentForm.invoiceId}
                onChange={handlePaymentFormChange}
                required
              >
                <option value="">--Seleccionar--</option>
                {allInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} - {formatCurrency(inv.amount)}
                  </option>
                ))}
              </select>

              <label>Monto a Pagar *</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={paymentForm.amount}
                onChange={handlePaymentFormChange}
                required
              />

              <label>Banco del Pago</label>
              <input
                type="text"
                name="bank"
                value={paymentForm.bank}
                onChange={handlePaymentFormChange}
              />

              <label>Nº Comprobante</label>
              <input
                type="text"
                name="voucherNumber"
                value={paymentForm.voucherNumber}
                onChange={handlePaymentFormChange}
              />

              <label>Descripción (opcional)</label>
              <input
                type="text"
                name="description"
                value={paymentForm.description}
                onChange={handlePaymentFormChange}
              />

              <div className="sa-modal-actions">
                <button type="submit" disabled={isSubmittingPayment}>
                  Guardar
                </button>
                <button type="button" onClick={closePaymentModal} disabled={isSubmittingPayment}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierAccount;